import React from 'react';

import { Box, Flex } from '@chakra-ui/react';
import loadable from '@loadable/component';
import PropTypes from 'prop-types';
import { matchRoutes, useLocation } from 'react-router-dom';

import ErrorBoundary from 'components/common/ErrorBoundary';
import InternalToolbar from 'components/internalToolbar';
import OnboardingHeader from 'components/layout/LayoutHeader/OnboardingHeader';

import Footer from './Footer';
import { useOnboardingTasks } from 'queries/onboardingTasks';
import { useFlags } from 'launchdarkly-react-client-sdk';

const NavigationV2 = loadable(() => import('components/layout/NavigationV2'));

const FullPageLayout = ({ children }) => {
  const { reflow } = useFlags();
  const location = useLocation();
  const { data: onboardingTasksData } = useOnboardingTasks();
  const onboardingComplete = onboardingTasksData?.metadata?.onboarding_complete;
  const noFooterRoutes = matchRoutes(
    [{ path: '/org/:id/incidents/:id/*' }, { path: '/incidents/:id/*' }],
    location,
  );

  // Base routes that should never show navigation
  const excludeNavRoutes = [
    { path: '/' },
    { path: '/registrations/new' },
    { path: '/sessions/new' },
    { path: '/sessions/sso/new' },
  ];

  // Integration onboarding routes with /settings/ included in the paths
  if (!onboardingComplete && reflow) {
    excludeNavRoutes.push({
      path: '/org/:orgId/settings/integrations/zoom/connections/:connectionId/onboarding',
    });
    excludeNavRoutes.push({
      path: '/org/:orgId/settings/integrations/webex/connections/:connectionId/onboarding',
    });
    excludeNavRoutes.push({
      path: '/org/:orgId/settings/integrations/google_calendar/connections/:connectionId/onboarding',
    });
    excludeNavRoutes.push({
      path: '/org/:orgId/settings/integrations/jira_cloud/site_selection/:id',
    });
    excludeNavRoutes.push({
      path: '/org/:orgId/settings/integrations/jira_cloud/connections/:connectionId/onboarding',
    });
    excludeNavRoutes.push({
      path: '/org/:orgId/settings/integrations/jira_cloud/connections/:connectionId/projects/:projectId/:configId/onboarding',
    });
    excludeNavRoutes.push({
      path: '/org/:orgId/settings/integrations/service_now/onboarding',
    });
    excludeNavRoutes.push({
      path: '/org/:orgId/settings/integrations/service_now/connections/:connectionId/onboarding',
    });
    excludeNavRoutes.push({
      path: '/org/:orgId/settings/integrations/service_now/connections/:connectionId/projects/:projectId/:configId/onboarding',
    });
    excludeNavRoutes.push({
      path: '/org/:orgId/settings/integrations/slack/connections/:connectionId/onboarding',
    });
  }

  // Check if current location matches any of the exclude routes
  const noNavRoutes = matchRoutes(excludeNavRoutes, location);

  let boxProps = {
    ml: 6,
    mr: 4,
    w: '100%',
    maxW: '1800px',
    px: ['2', '2', '3', '4'],
    py: 3,
    m: 0,
  };

  if (noFooterRoutes) {
    boxProps.pb = 0;
  }

  return (
    <>
      <InternalToolbar />
      <Flex minHeight="100vh" width="100%" flexDirection={'column'}>
        {!noNavRoutes && <NavigationV2 />}

        <OnboardingHeader pathname={location.pathname} />

        <Flex
          flex="1"
          direction="column"
          justifyContent="space-between"
          alignItems={'center'}
        >
          <Box flex="1" {...boxProps}>
            <ErrorBoundary>{children}</ErrorBoundary>
          </Box>
          {!noFooterRoutes && (
            <>
              <Flex w="100%" justifyContent="flex-end">
                <Footer />
              </Flex>
            </>
          )}
        </Flex>
      </Flex>
    </>
  );
};

FullPageLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

export default FullPageLayout;
